import React from 'react';
import { Box, Link, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import { formatCurrency, formatDate } from '../../../../utils/formatters';
import ArticuloInfo360DocumentoList from './ArticuloInfo360DocumentoList';
import type { ArticuloInfo360ActividadItem, ArticuloInfo360Documento, ArticuloInfo360Indicadores } from '../types';
import { resolverAccionDocumentoArticulo } from '../documentoAcciones';
import { formatCantidad, resolveUnidadMedida } from '../formatCantidad';

interface Props {
  data: ArticuloInfo360Indicadores;
  onAbrirDocumento?: (documento: ArticuloInfo360Documento) => void;
  onAbrirActividad?: (actividad: ArticuloInfo360ActividadItem) => void;
}

const ArticuloInfo360TabActividad: React.FC<Props> = ({ data, onAbrirDocumento, onAbrirActividad }) => {
  const um = resolveUnidadMedida(data.unidadMedida);

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
          Actividad reciente
        </Typography>
        {data.actividadReciente.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Sin actividad reciente.
          </Typography>
        ) : (
          <Stack spacing={1.25}>
            {data.actividadReciente.map((item, index) => {
              const accion = onAbrirActividad ? resolverAccionDocumentoArticulo(item) : null;
              return (
                <Stack
                  key={`${item.tipo}-${item.descripcion}-${item.fecha}-${index}`}
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  spacing={0.5}
                >
                  <Box minWidth={0}>
                    {accion ? (
                      <Tooltip title={accion.label}>
                        <Link
                          component="button"
                          type="button"
                          underline="hover"
                          onClick={() => onAbrirActividad?.(item)}
                          aria-label={`${accion.label}: ${item.descripcion}`}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            font: 'inherit',
                            fontWeight: 600,
                            textAlign: 'left',
                          }}
                        >
                          {item.descripcion}
                          <OpenInNew sx={{ fontSize: 14 }} aria-hidden />
                        </Link>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" fontWeight={600}>{item.descripcion}</Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" display="block">
                      {item.tipo} · {formatDate(item.fecha)}
                      {item.cantidad != null ? ` · ${formatCantidad(item.cantidad, um)}` : ''}
                    </Typography>
                  </Box>
                  {item.importe != null && item.importe !== 0 ? (
                    <Typography variant="body2" fontWeight={600}>
                      {formatCurrency(item.importe)}
                    </Typography>
                  ) : null}
                </Stack>
              );
            })}
          </Stack>
        )}
      </Paper>

      <ArticuloInfo360DocumentoList
        titulo="Últimas compras"
        documentos={data.ultimasCompras}
        emptyText="No hay compras recientes."
        onAbrirDocumento={onAbrirDocumento}
        unidadMedida={um}
      />

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
          Movimientos de stock
        </Typography>
        {data.movimientosRecientes.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Sin movimientos recientes.
          </Typography>
        ) : (
          <Stack spacing={1.25}>
            {data.movimientosRecientes.map((mov) => (
              <Stack
                key={`${mov.fecha}-${mov.descripcion}-${mov.cantidad}`}
                direction="row"
                justifyContent="space-between"
                spacing={1}
              >
                <Box minWidth={0}>
                  <Typography variant="body2" fontWeight={600}>{mov.descripcion}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {mov.tipo} · {formatDate(mov.fecha)}
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight={600}>
                  {formatCantidad(mov.cantidad, um)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </Paper>
    </Stack>
  );
};

export default ArticuloInfo360TabActividad;
